import FallbackImage16x9 from '@assets/fallback-images/16x9.png'
import FallbackImage2x3 from '@assets/fallback-images/2x3.png'
import { Image } from 'cloudflare/resources/images/v1/v1'

import { FieldValues, UseFormSetValue } from '@cedarjs/forms'

export { FallbackImage16x9, FallbackImage2x3 }

export type TransformImageOptions = {
  // Any CSS color code for transparent image background fill.
  background?: string
  // Percentage to blur the image (1 - 100).
  blur?: number
  fit?: 'cover' | 'crop' | string
  format?: 'avif' | 'jpeg' | 'jpg' | 'png' | 'webp' | string
  // Supported string value: set of x and y coordinates in the format `{x}x{y}` (e.g., `0.5x0.5`).
  gravity?: 'auto' | 'bottom' | 'left' | 'top' | 'right' | string
  height?: number
  // Image quality percentage (1 - 100).
  quality?: number
  width?: number
}

export type ImageWithProfile = {
  image: string
  profile: string
}

/**
 * Get an image from an array of images and profiles.
 */
export const getImage = (
  images: Array<{ image: string; profile: string }> = [],
  profile: string = ImageProfile.PBS_SHOW_POSTER,
  useFallback = true,
  transformOptions?: TransformImageOptions
): ImageWithProfile | null => {
  const getFallbackImage = () => {
    if (profile.includes('2x3')) {
      return { image: FallbackImage2x3, profile }
    }
    if (profile.includes('16x9')) {
      return { image: FallbackImage16x9, profile }
    }
    return null
  }
  let image = images?.findLast((i) => i.profile == profile)

  // For PBS mezzanine images, try to fall back on kids profiles
  // if the regular profiles as not found. For normalized results
  // this is handled in the `images` resolver, but the web side
  // looks at `PbsShow.images` and `PbsVideo.images` directly to
  // profile configuration forms for admin users.
  if (!image) {
    if (profile == ImageProfile.PBS_SHOW_MEZZANINE) {
      image = images?.findLast(
        (i) => i.profile == ImageProfile.PBS_SHOW_MEZZANINE_KIDS
      )
      if (image) {
        image = { ...image, profile: ImageProfile.PBS_SHOW_MEZZANINE }
      }
    } else if (profile == ImageProfile.PBS_VIDEO_MEZZANINE) {
      image = images?.findLast(
        (i) => i.profile == ImageProfile.PBS_VIDEO_MEZZANINE_KIDS
      )
      if (image) {
        image = { ...image, profile: ImageProfile.PBS_VIDEO_MEZZANINE }
      }
    }
  }

  if (image && transformOptions) {
    image = {
      ...image,
      image: transformImage({
        imageUrl: image.image,
        options: transformOptions,
      }),
    }
  }
  return image ? image : useFallback ? getFallbackImage() : null
}

/**
 * Transforms images with support for Cloudflare Images and PBS ITS image hosting.
 *
 * Transform options keys are mapped directly to Cloudflare Images transform URL parameters.
 *
 * TODO: Eliminate this in favor of GraphQL transforms input in queries.
 *
 * @see api/src/lib/images
 * @see https://developers.cloudflare.com/images/transform-images/transform-via-url/
 */
export const transformImage = ({
  imageUrl,
  options,
}: {
  imageUrl: string
  options: TransformImageOptions
}) => {
  if (options?.format?.toLowerCase() == 'jpg') {
    options.format = 'jpeg'
  }
  if (
    (options?.fit?.toLowerCase() == 'crop' ||
      options?.fit?.toLowerCase() == 'cover') &&
    !options?.gravity
  ) {
    options.gravity = 'auto'
  }

  // Remove any trailing slashes from the URL to prevent creating invalid URLs
  // with multiple slashes.
  imageUrl = imageUrl.replace(/\/$/, '')
  const cloudflareOptions = []
  for (const [key, value] of Object.entries(options)) {
    if (value) {
      cloudflareOptions.push(
        `${key}=${encodeURIComponent(value).toLowerCase()}`
      )
    }
  }
  if (cloudflareOptions.length > 0) {
    const baseUrl = `${process.env.CLOUDFLARE_IMAGES_TRANSFORM_URL}/${cloudflareOptions.join(',')}`
    if (imageUrl.startsWith('https://image.pbs.org/')) {
      imageUrl = `${baseUrl}/${imageUrl}`
    } else if (imageUrl.startsWith(process.env.CLOUDFLARE_IMAGES_BASE_URL)) {
      // Get the image ID from the image delivery URL.
      try {
        const url = new URL(imageUrl)
        const imageId = url.pathname.split('/').pop()
        imageUrl = `${baseUrl}/${imageId}`
      } catch (error) {
        console.warn(
          { imageUrl, error },
          'Cloudflare Images image URL generation error'
        )
      }
    }
  }
  return imageUrl
}

export type UploadImagesAndUpdateFormImage = {
  key: string
  file: File
  uploadUrl: string
}

export const uploadImagesAndUpdateForm = async (
  images: Array<UploadImagesAndUpdateFormImage>,
  setValue: UseFormSetValue<FieldValues>,
  isVideo = false
) => {
  const uploadImage = async (image: UploadImagesAndUpdateFormImage) => {
    const { key, file, uploadUrl } = image
    const imageUrl = await uploadImageToCloudflare(uploadUrl, file)
    const profile = isVideo
      ? ImageProfile.PBS_VIDEO_MEZZANINE
      : key === 'customMezzanineImage'
        ? ImageProfile.PBS_SHOW_MEZZANINE
        : key === 'customBackgroundImage'
          ? ImageProfile.PBS_SHOW_BACKGROUND
          : ImageProfile.PBS_SHOW_POSTER
    setValue(key, { image: imageUrl, profile })
  }

  const uploads = images.map(async (image) => {
    await uploadImage(image)
  })

  return Promise.all(uploads)
}

/**
 * Mutation for creating an image upload URL used by uploadImage.
 *
 * @see uploadImage
 */
export const CREATE_IMAGE_UPLOAD_URL_MUTATION = gql`
  mutation CreateImageUploadUrl {
    createImageUploadUrl {
      id
      uploadUrl
    }
  }
`

/**
 * Uploads an image to Cloudflare and returns the base image URL.
 *
 * This URL will not work on it's own. A variant or transform must be appended.
 *
 * @see https://developers.cloudflare.com/images/upload-images/upload-url/
 */
export const uploadImageToCloudflare = async (
  imageUploadUrl: string,
  file: File
): Promise<string> => {
  const body = new FormData()
  body.append('file', file)
  const uploadResult = await fetch(imageUploadUrl, { method: 'post', body })
  const response = (await uploadResult.json()) as { result: Image }
  return `${process.env.CLOUDFLARE_IMAGES_BASE_URL}/${response.result.id}`
}

/**
 * Creates a simple URL to preview an uploaded image.
 */
export const createUploadedImagePreviewUrl = ({
  uploadedImageUrl,
  width,
}: {
  uploadedImageUrl: string
  width: number
}): string => {
  if (uploadedImageUrl.startsWith(process.env.CLOUDFLARE_IMAGES_BASE_URL)) {
    // Get the image ID from the image delivery URL.
    try {
      const url = new URL(uploadedImageUrl)
      const imageId = url.pathname.split('/').pop()
      return `${process.env.CLOUDFLARE_IMAGES_TRANSFORM_URL}/width=${width}/${imageId}`
    } catch (error) {
      console.warn(
        { uploadedImageUrl, error },
        'Cloudflare Images image preview URL generation error'
      )
    }
  }
  throw new Error('Unsupported uploaded image URL')
}

/**
 * Image profiles.
 *
 * @see api/src/lib/image
 */
export enum ImageProfile {
  HERO_MEZZANINE = 'hero-mezzanine',
  HERO_MOBILE = 'hero-mobile',
  PBS_SHOW_BACKGROUND = 'background',
  PBS_SHOW_MEZZANINE = 'show-mezzanine16x9',
  PBS_SHOW_MEZZANINE_KIDS = 'show-kids-16x9',
  PBS_SHOW_POSTER = 'show-poster2x3',
  PBS_VIDEO_MEZZANINE = 'asset-mezzanine-16x9',
  PBS_VIDEO_MEZZANINE_KIDS = 'asset-kids-mezzanine1-16x9',
  STATION_BACKGROUND = 'station-background',
  STATION_COLOR_LOGO = 'station-color-logo',
  STATION_FAVICON = 'station-favicon',
  STATION_LOGO_PRIMARY = 'station-logo-primary',
  STATION_LOGO_SECONDARY = 'station-logo-secondary',
  STATION_PASSPORT_LOGO = 'station-passport-logo',
  STATION_WHITE_LOGO = 'station-white-logo',
}
